import React, { useState, useRef } from 'react';
import supabase from '../../supabase/supabase';
import { useAppData } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export default function ProfileSettings({ onClose }) {
  const { profile, setProfile, session, refreshProfile } = useAppData();
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [avatarInputType, setAvatarInputType] = useState('file');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => setAvatarUrl(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const { error } = await supabase
        .from('profiles_20240520')
        .update({ 
          full_name: fullName,
          avatar_url: avatarUrl 
        })
        .eq('id', session.user.id);

      if (error) throw error;
      setProfile({ ...profile, full_name: fullName, avatar_url: avatarUrl });
      setMessage('Profile updated successfully!');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setMessage('Password updated successfully!');
      setNewPassword('');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
      <div className="bg-app-surface border border-app-border rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-app-border">
          <h2 className="text-xl font-bold flex items-center gap-2 text-white">
            <SafeIcon icon={FiIcons.FiUser} className="text-app-accent" />
            Account Settings
          </h2>
          <button onClick={onClose} className="text-app-muted hover:text-white"><FiIcons.FiX size={24}/></button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Identity Section */}
          <section className="space-y-4">
            <h3 className="text-xs font-black text-app-accent uppercase tracking-widest">Public Identity</h3>
            <div className="space-y-4 bg-app-bg/50 p-4 rounded-lg border border-app-border">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-app-muted">Display Name</label>
                <input 
                  type="text" 
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="w-full"
                  placeholder="Set your terminal name"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-6 pt-2">
                <div className="relative group">
                  <div className="w-20 h-20 rounded-full border-2 border-app-border overflow-hidden bg-app-bg flex items-center justify-center shrink-0">
                    {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : <FiIcons.FiUser className="text-3xl text-app-muted" />}
                  </div>
                  <button onClick={() => { setAvatarInputType('file'); fileInputRef.current?.click(); }} className="absolute bottom-0 right-0 p-1.5 bg-app-accent text-black rounded-full shadow-lg">
                    <FiIcons.FiCamera size={12} />
                  </button>
                  <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                </div>
                
                <div className="flex-1 w-full space-y-3">
                  <div className="flex items-center gap-2 p-1 bg-app-bg rounded-lg border border-app-border">
                    <button onClick={() => setAvatarInputType('file')} className={`flex-1 py-1 text-[9px] font-bold uppercase rounded ${avatarInputType === 'file' ? 'bg-app-accent text-black' : 'text-app-muted'}`}>Upload</button>
                    <button onClick={() => setAvatarInputType('url')} className={`flex-1 py-1 text-[9px] font-bold uppercase rounded ${avatarInputType === 'url' ? 'bg-app-accent text-black' : 'text-app-muted'}`}>URL</button>
                  </div>
                  {avatarInputType === 'url' && (
                    <input 
                      type="url" 
                      value={avatarUrl.startsWith('data:') ? '' : avatarUrl}
                      onChange={e => setAvatarUrl(e.target.value)}
                      placeholder="Image URL"
                      className="w-full text-[10px] font-mono py-1"
                    />
                  )}
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <button onClick={handleUpdateProfile} disabled={loading} className="bg-app-accent px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest text-black hover:bg-yellow-500 transition-colors">
                  {loading ? 'Saving...' : 'Update Profile'}
                </button>
              </div>
            </div>
          </section>

          {/* Security Section */}
          <section className="space-y-4 border-t border-app-border pt-4">
            <h3 className="text-xs font-black text-app-accent uppercase tracking-widest">Security</h3>
            <form onSubmit={handleChangePassword} className="space-y-3 bg-app-bg/50 p-4 rounded-lg border border-app-border">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-app-muted">New Password</label>
                <div className="flex gap-2">
                  <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="flex-1 text-sm" placeholder="••••••••" />
                  <button type="submit" disabled={loading} className="bg-app-surface border border-app-border px-4 py-1 rounded text-[10px] font-black uppercase hover:bg-app-red hover:text-white transition-colors">Update</button>
                </div>
              </div>
            </form>
          </section>

          {message && (
            <div className={`p-3 rounded text-[10px] uppercase font-black tracking-widest text-center border ${message.toLowerCase().includes('success') ? 'bg-app-green/10 text-app-green border-app-green/20' : 'bg-app-red/10 text-app-red border-app-red/20'}`}>{message}</div>
          )}
        </div>

        <div className="p-4 bg-app-bg/50 border-t border-app-border flex justify-end">
          <button onClick={onClose} className="text-xs font-bold text-app-muted hover:text-white uppercase tracking-widest">Dismiss</button>
        </div>
      </div>
    </div>
  );
}